import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { QrCode, Camera, X, CheckCircle, AlertTriangle, MapPin, Loader2, Shield, Sparkles, Wrench, User, ArrowLeft } from 'lucide-react';
import QrScanner from 'qr-scanner';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Scanner = () => {
  const { user, isAdmin, isOrgAdmin, isRangeOfficer } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [lastScannedCode, setLastScannedCode] = useState(null);
  const [lastScanTime, setLastScanTime] = useState(null);
  const [scanAttempts, setScanAttempts] = useState(0);
  
  // Activity selection state
  const [showActivitySelection, setShowActivitySelection] = useState(false);
  const [activityTypes, setActivityTypes] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [pendingSessionData, setPendingSessionData] = useState(null);
  
  // Weapon scanning state
  const [showWeaponActions, setShowWeaponActions] = useState(false);
  const [scannedWeapon, setScannedWeapon] = useState(null);
  const [weaponActionLoading, setWeaponActionLoading] = useState(false);
  const [weaponActionSuccess, setWeaponActionSuccess] = useState(null);
  const [cleaningNotes, setCleaningNotes] = useState('');
  const [maintenanceDescription, setMaintenanceDescription] = useState('');
  const [maintenanceCost, setMaintenanceCost] = useState('');
  const [showCleaningForm, setShowCleaningForm] = useState(false);
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false);
  const [showLoanForm, setShowLoanForm] = useState(false);
  
  // Debounce settings - prevent multiple scans within this timeframe
  const SCAN_DEBOUNCE_MS = 3000; // 3 seconds between scans of same code

  // Refs exactly like Bolt.ai
  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);
  
  // CRITICAL FIX: Use ref for processing flag to prevent race conditions
  // React state updates are async, so we use ref for synchronous checking
  const isProcessingRef = useRef(false);

  // Load activity types from organization
  useEffect(() => {
    const fetchActivityTypes = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE}/api/organizations/${user?.organization_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const types = response.data?.activity_types || ['Trening', 'Kurs', 'Stevne'];
        setActivityTypes(types);
        console.log('📋 Activity types loaded:', types);
      } catch (error) {
        console.error('Failed to load activity types:', error);
        // Fallback to default types
        setActivityTypes(['Trening', 'Kurs', 'Stevne']);
      }
    };

    if (user?.organization_id) {
      fetchActivityTypes();
    }
  }, [user]);

  // AUTO-START when component mounts (like Bolt.ai)
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('🎯 Auto-starting scanner...');
      setScanning(true);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Effect to start/stop scanning based on scanning state
  useEffect(() => {
    if (scanning) {
      startScanning();
    } else {
      cleanup();
    }
  }, [scanning]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  // Auto-clear "already registered" error popup after 5 seconds (same as success timing)
  useEffect(() => {
    if (error && error.includes('allerede registrert')) {
      const clearTimer = setTimeout(() => {
        console.log('🔄 Auto-clearing "already registered" error popup...');
        setError(null);
      }, 5000);
      
      return () => clearTimeout(clearTimer);
    }
  }, [error]);

  // Check if cached camera permission is still valid (within 30 days)
  const isCameraPermissionValid = () => {
    const storedPermission = localStorage.getItem('aktivlogg_camera_permission');
    const storedTimestamp = localStorage.getItem('aktivlogg_camera_timestamp');
    
    if (!storedPermission || !storedTimestamp) {
      return false;
    }
    
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const timeDiff = now - parseInt(storedTimestamp);
    
    if (timeDiff > thirtyDaysInMs) {
      // Permission cache expired, clear it
      localStorage.removeItem('aktivlogg_camera_permission');
      localStorage.removeItem('aktivlogg_camera_timestamp');
      return false;
    }
    
    return storedPermission === 'granted';
  };

  // Function to check camera permissions first with persistent storage
  const checkCameraPermissions = async () => {
    try {
      // Check if camera is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera API not supported');
      }

      // Check if permissions are already granted and still valid
      const hasValidPermission = isCameraPermissionValid();
      
      // Check current permission status
      const permissionStatus = await navigator.permissions?.query({ name: 'camera' });
      console.log('📹 Camera permission status:', permissionStatus?.state);

      // If permission is granted or was previously granted and still valid, try to use camera
      if (permissionStatus?.state === 'granted' || hasValidPermission) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: { facingMode: 'environment' } 
          });
          
          console.log('✅ Camera access granted (cached or current)');
          
          // Show brief success message for cached permission
          if (hasValidPermission && permissionStatus?.state !== 'granted') {
            console.log('📱 Using cached camera permission');
          }
          
          // Store successful permission
          localStorage.setItem('aktivlogg_camera_permission', 'granted');
          localStorage.setItem('aktivlogg_camera_timestamp', Date.now().toString());
          
          // Stop the test stream
          stream.getTracks().forEach(track => track.stop());
          
          return true;
        } catch (streamError) {
          // Permission might have been revoked, clear cache
          localStorage.removeItem('aktivlogg_camera_permission');
          throw streamError;
        }
      }

      // If no cached permission, request camera access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      
      console.log('✅ Camera access granted (new)');
      
      // Store successful permission with timestamp
      localStorage.setItem('aktivlogg_camera_permission', 'granted');
      localStorage.setItem('aktivlogg_camera_timestamp', Date.now().toString());
      
      // Stop the test stream
      stream.getTracks().forEach(track => track.stop());
      
      return true;
    } catch (error) {
      console.error('❌ Camera permission check failed:', error);
      
      // Clear any cached permission on error
      localStorage.removeItem('aktivlogg_camera_permission');
      
      // Try front camera if back camera fails
      try {
        const frontStream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: 'user' } 
        });
        console.log('✅ Front camera access granted');
        
        // Store successful front camera permission
        localStorage.setItem('aktivlogg_camera_permission', 'granted');
        localStorage.setItem('aktivlogg_camera_timestamp', Date.now().toString());
        
        frontStream.getTracks().forEach(track => track.stop());
        return true;
      } catch (frontError) {
        console.error('❌ Front camera also failed:', frontError);
        localStorage.removeItem('aktivlogg_camera_permission');
        return false;
      }
    }
  };

  // Function to initialize and start the QR scanner
  const startScanning = async () => {
    try {
      console.log('🎯 Starting QR Scanner...');

      if (!videoRef.current) {
        throw new Error('Video element not found');
      }

      // Check camera permissions first
      const cameraAvailable = await checkCameraPermissions();
      if (!cameraAvailable) {
        setError('Kunne ikke få tilgang til kamera. Sjekk browserinnstillinger og tillatelser.');
        return;
      }

      // Clear any existing error
      setError(null);

      // Initialize QrScanner with back camera
      qrScannerRef.current = new QrScanner(
        videoRef.current,
        (result) => {
          console.log('🎉 QR Code detected:', result.data);
          handleScan(result.data);
        },
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: 'environment'
        }
      );

      // Start the scanning process
      await qrScannerRef.current.start();
      console.log('✅ QR Scanner started successfully');

    } catch (error) {
      console.error('❌ Failed to start QR scanner:', error);

      // Fallback to front camera
      try {
        console.log('🔄 Trying front camera...');
        if (qrScannerRef.current) {
          qrScannerRef.current.destroy();
        }

        qrScannerRef.current = new QrScanner(
          videoRef.current,
          (result) => {
            console.log('🎉 QR Code detected (front cam):', result.data);
            handleScan(result.data);
          },
          {
            returnDetailedScanResult: true,
            highlightScanRegion: true,
            highlightCodeOutline: true,
            preferredCamera: 'user'
          }
        );

        await qrScannerRef.current.start();
        console.log('✅ QR Scanner started with front camera');

      } catch (frontCameraError) {
        console.error('❌ Both cameras failed:', frontCameraError);
        let errorMessage = 'Kunne ikke starte kamera.';
        
        if (frontCameraError.name === 'NotAllowedError') {
          errorMessage += ' Du må gi tilgang til kamera i browserinnstillingene.';
        } else if (frontCameraError.name === 'NotFoundError') {
          errorMessage += ' Ingen kamera funnet på enheten.';
        } else if (frontCameraError.name === 'NotSupportedError') {
          errorMessage += ' Kamera ikke støttet i denne browseren.';
        } else {
          errorMessage += ' Sjekk at du har gitt tilgang til kamera.';
        }
        
        setError(errorMessage);
      }
    }
  };

  // Function to clean up the QR scanner instance (Bolt.ai approach)
  const cleanup = () => {
    console.log('🧹 Cleaning up QR scanner...');
    if (qrScannerRef.current) {
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
  };

  // Handles the raw data from a detected QR code (Enhanced debugging)
  const handleScan = async (scannedText) => {
    console.log('=== QR SCAN DETECTED ===');
    console.log('Raw scanned text:', scannedText);
    console.log('isProcessing (state):', isProcessing);
    console.log('isProcessingRef (ref):', isProcessingRef.current);
    
    // CRITICAL FIX: Check ref first (synchronous) to prevent race conditions
    if (!scannedText || isProcessingRef.current) {
      console.log('⏸️ Scan ignored - no text or already processing');
      return;
    }

    // ROBUST duplicate scan prevention - check both time AND processing state
    const now = Date.now();
    const isSameCode = lastScannedCode === scannedText;
    const isWithinDebounceTime = lastScanTime && (now - lastScanTime) < SCAN_DEBOUNCE_MS;
    
    if (isSameCode && isWithinDebounceTime) {
      console.log(`⚠️ Duplicate scan prevented - same code "${scannedText}" scanned within ${SCAN_DEBOUNCE_MS}ms`);
      console.log(`⏰ Time since last scan: ${now - lastScanTime}ms`);
      return;
    }

    // CRITICAL FIX: Set ref FIRST (synchronous), then state (async)
    // This prevents race conditions where multiple scans happen before state updates
    isProcessingRef.current = true;
    console.log('🔒 Processing lock set (ref):', isProcessingRef.current);
    
    // Update tracking variables immediately to prevent race conditions
    setLastScannedCode(scannedText);
    setLastScanTime(now);
    
    // Also set processing state for UI updates
    setIsProcessing(true);

    // Increment scan attempts for debugging
    setScanAttempts(prev => prev + 1);
    console.log(`🔍 Scan attempt ${scanAttempts + 1}:`, scannedText);

    try {
      let code = scannedText.trim();
      console.log('📝 Step 1 - Raw QR text (trimmed):', code);

      // Handle QR codes that are URLs containing the code as a parameter (Bolt.ai logic)
      if (code.includes('scanner?c=') || code.includes('scanner?code=')) {
        const urlParams = new URLSearchParams(code.split('?')[1]);
        code = urlParams.get('c') || urlParams.get('code') || code;
        console.log('🔗 Step 2 - Extracted code from scanner URL:', code);
      }

      // Handle full URLs by parsing them and extracting the code parameter
      if (code.includes('http')) {
        try {
          const url = new URL(code);
          const params = new URLSearchParams(url.search);
          const extracted = params.get('c') || params.get('code') || params.get('qr_data');
          if (extracted) {
            code = extracted;
            console.log('🌐 Step 3 - Extracted code from full URL:', code);
          } else {
            console.log('🌐 No URL parameters found, keeping full URL as code');
          }
        } catch (urlError) {
          console.log('🌐 Not a valid URL, using as-is:', urlError.message);
        }
      }

      // Handle Aktivlogg format
      if (code.startsWith('aktivlogg://scan/')) {
        code = code.replace('aktivlogg://scan/', '');
        console.log('📱 Step 4 - Extracted code from aktivlogg URL:', code);
      }

      // Handle direct UUID format (most common for our system)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (uuidRegex.test(code)) {
        console.log('🆔 Step 5 - Valid UUID format detected:', code);
      } else {
        console.log('⚠️ Step 5 - Not standard UUID format, trying anyway:', code);
      }

      console.log('🎯 FINAL CODE TO SEND TO BACKEND:', code);

      if (!code) {
        throw new Error('Tom QR-kode skannet');
      }

      // Check if this is a weapon QR code (new format)
      if (code.startsWith('AKT-WPN-')) {
        console.log('🔫 Weapon QR code detected:', code);
        await handleWeaponQRCode(code);
      } 
      // Check if this is a weapon QR code (old JSON format)
      else {
        let isWeaponJson = false;
        try {
          const parsed = JSON.parse(code);
          if (parsed.type === 'weapon' && parsed.id) {
            console.log('🔫 Old format weapon QR detected, ID:', parsed.id);
            isWeaponJson = true;
            await handleWeaponQRCodeById(parsed.id);
          }
        } catch (e) {
          // Not JSON, continue with normal QR handling
        }
        
        if (!isWeaponJson) {
          await handleQRCode(code);
        }
      }

    } catch (error) {
      console.error('❌ QR Scan Error:', error);
      
      let errorMsg = 'Ukjent feil ved skanning av QR-kode';
      
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.message) {
        errorMsg = error.message;
      }

      setError(errorMsg);
      setScanning(false);
      setIsProcessing(false);
      isProcessingRef.current = false; // CRITICAL: Also reset ref
    }
  };

  // Function to complete registration after activity selection
  const completeRegistration = async (activity) => {
    if (!pendingSessionData) return;

    try {
      setIsProcessing(true);
      console.log('📝 Completing registration with activity:', activity);

      const payload = { 
        qr_data: pendingSessionData.qr_data,
        activity: activity
      };

      const response = await axios.post(`${API_BASE}/api/training/scan`, payload, {
        headers: {
          'Authorization': `Bearer ${pendingSessionData.token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('✅ Registration complete:', response.data);

      // Show success
      const ti = response.data.team_info;
      setResult({
        success: true,
        message: ti?.team ? `${response.data.message} Du er på Lag ${ti.team} (${ti.event_title}).` : response.data.message,
        session: response.data.session,
        location: response.data.location
      });
      if (ti?.team) toast.success(`Du er på Lag ${ti.team}`, { description: ti.event_title, duration: 6000 });

      setSuccess(true);
      setShowActivitySelection(false);
      setIsProcessing(false);

      // Redirect after delay
      setTimeout(() => {
        window.location.href = '/log';
      }, 5000);

    } catch (error) {
      console.error('❌ Registration error:', error);
      let errorMsg = 'Det oppstod en feil. Vennligst prøv igjen.';
      
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      setError(errorMsg);
      setShowActivitySelection(false);
      setIsProcessing(false);
    }
  };

  // Main function to process the validated QR code
  const handleQRCode = async (code) => {
    console.log('=== HANDLE QR CODE CALLED ===');
    console.log('Code received:', code);
    console.log('isProcessing (state):', isProcessing);
    console.log('isProcessingRef (ref):', isProcessingRef.current);

    // REMOVED: Don't check here - already checked in handleScan
    // The ref is already set in handleScan, so we can proceed
    
    console.log('🔒 Processing lock already set in handleScan');
    setError(null);

    try {
      console.log('=== STARTING QR CODE PROCESSING ===');
      console.log('Backend URL:', API_BASE);
      console.log('Full API endpoint:', `${API_BASE}/api/training/scan`);
      console.log('QR Code to send:', code);

      // Send scan request to backend
      const token = localStorage.getItem('token');
      console.log('Auth token available:', !!token);
      
      const payload = { qr_data: code };
      console.log('Request payload:', JSON.stringify(payload, null, 2));

      // Instead of immediately registering, show activity selection
      console.log('✅ QR code validated, showing activity selection');
      
      // Store the pending session data
      setPendingSessionData({ qr_data: code, token });
      
      // Show activity selection screen
      setShowActivitySelection(true);
      setScanning(false);
      setIsProcessing(false);
      isProcessingRef.current = false;

    } catch (error) {
      console.error('❌ Processing Error Details:');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.error('Response status:', error.response?.status);
      console.error('Response data:', error.response?.data);
      console.error('Request config:', error.config);
      
      let errorMsg = 'Det oppstod en feil. Vennligst prøv igjen.';
      
      if (error.response?.status === 404) {
        errorMsg = 'QR-koden ble ikke gjenkjent. Sjekk at du skanner riktig kode for din organisasjon.';
      } else if (error.response?.status === 403) {
        errorMsg = 'Du har ikke tilgang til denne lokasjonen.';
      } else if (error.response?.status === 400) {
        const backendError = error.response.data?.detail || 'Ugyldig forespørsel';
        
        // Check if it's "already registered" error
        if (backendError.includes('allerede registrert')) {
          errorMsg = backendError; // Will trigger special popup
        } else {
          errorMsg = backendError;
        }
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      console.log('Final error message:', errorMsg);
      
      // Use setError instead of toast for better UX
      if (errorMsg.includes('allerede registrert')) {
        setError(errorMsg); // Show error popup for "already registered" 
      } else {
        setError(errorMsg);
      }
      
      setScanning(false); // Stop scanning on error
      
      // Keep processing locked for longer after API error
      setTimeout(() => {
        console.log('🔓 Releasing processing lock after API error...');
        setIsProcessing(false);
        isProcessingRef.current = false; // CRITICAL: Also reset ref
      }, 2000);
    }
  };

  const resetScanner = () => {
    setResult(null);
    setError(null);
    setIsProcessing(false);
    isProcessingRef.current = false; // CRITICAL: Also reset ref
    setSuccess(false);
    setLastScannedCode(null);
    setScanAttempts(0);
  };

  // Handle weapon QR code scanning
  const handleWeaponQRCode = async (qrCode) => {
    console.log('🔫 Processing weapon QR code:', qrCode);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/club-operations/weapons/scan/${qrCode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Weapon found:', response.data);
      setScannedWeapon(response.data);
      setShowWeaponActions(true);
      setScanning(false);
      setIsProcessing(false);
      isProcessingRef.current = false;
      
    } catch (error) {
      console.error('❌ Weapon scan error:', error);
      let errorMsg = 'Kunne ikke finne våpenet.';
      
      if (error.response?.status === 404) {
        errorMsg = 'Våpenet ble ikke funnet. Sjekk at QR-koden tilhører din klubb.';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      setError(errorMsg);
      setScanning(false);
      setIsProcessing(false);
      isProcessingRef.current = false;
    }
  };

  // Handle weapon QR code by ID (old JSON format)
  const handleWeaponQRCodeById = async (weaponId) => {
    console.log('🔫 Processing weapon by ID:', weaponId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/club-operations/weapons/${weaponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Weapon found:', response.data);
      setScannedWeapon(response.data);
      setShowWeaponActions(true);
      setScanning(false);
      setIsProcessing(false);
      isProcessingRef.current = false;
      
    } catch (error) {
      console.error('❌ Weapon scan error:', error);
      let errorMsg = 'Kunne ikke finne våpenet.';
      
      if (error.response?.status === 404) {
        errorMsg = 'Våpenet ble ikke funnet. Sjekk at QR-koden tilhører din klubb.';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      setError(errorMsg);
      setScanning(false);
      setIsProcessing(false);
      isProcessingRef.current = false;
    }
  };

  // Handle weapon cleaning registration
  const handleWeaponCleaning = async () => {
    if (!scannedWeapon) return;
    
    setWeaponActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/api/club-operations/weapons/${scannedWeapon.id}/clean`, {
        notes: cleaningNotes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWeaponActionSuccess({
        type: 'cleaning',
        message: 'Pussing registrert!',
        weapon: scannedWeapon
      });
      setShowCleaningForm(false);
      setCleaningNotes('');
      
    } catch (error) {
      console.error('❌ Cleaning registration error:', error);
      setError(error.response?.data?.detail || 'Kunne ikke registrere pussing');
    } finally {
      setWeaponActionLoading(false);
    }
  };

  // Handle weapon maintenance registration
  const handleWeaponMaintenance = async () => {
    if (!scannedWeapon || !maintenanceDescription) return;
    
    setWeaponActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/api/club-operations/weapons/${scannedWeapon.id}/maintenance`, {
        description: maintenanceDescription,
        cost: maintenanceCost ? parseFloat(maintenanceCost) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWeaponActionSuccess({
        type: 'maintenance',
        message: 'Vedlikehold registrert!',
        weapon: scannedWeapon
      });
      setShowMaintenanceForm(false);
      setMaintenanceDescription('');
      setMaintenanceCost('');
      
    } catch (error) {
      console.error('❌ Maintenance registration error:', error);
      setError(error.response?.data?.detail || 'Kunne ikke registrere vedlikehold');
    } finally {
      setWeaponActionLoading(false);
    }
  };

  // Reset weapon scanner state
  const resetWeaponScanner = () => {
    setShowWeaponActions(false);
    setScannedWeapon(null);
    setWeaponActionSuccess(null);
    setShowCleaningForm(false);
    setShowMaintenanceForm(false);
    setShowLoanForm(false);
    setCleaningNotes('');
    setMaintenanceDescription('');
    setMaintenanceCost('');
    setError(null);
    setScanning(true);
  };

  // Render weapon action success screen
  if (weaponActionSuccess) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-green-50 rounded-3xl max-w-sm w-full mx-4 shadow-2xl border-4 border-green-200">
          <div className="flex flex-col items-center p-8 text-center">
            <div className="bg-green-500 p-4 rounded-full mb-6">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            
            <div className="bg-green-500 text-white px-6 py-3 rounded-full mb-6 shadow-lg">
              <span className="text-lg font-bold">
                {weaponActionSuccess.type === 'cleaning' ? '✨ PUSSING REGISTRERT!' : '🔧 VEDLIKEHOLD REGISTRERT!'}
              </span>
            </div>
            
            <div className="bg-white rounded-2xl p-6 mb-6 w-full shadow-inner border-2 border-green-100">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Våpen:</span>
                  <span className="text-gray-900 font-bold">{weaponActionSuccess.weapon.brand} {weaponActionSuccess.weapon.model}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Kaliber:</span>
                  <span className="text-gray-900">{weaponActionSuccess.weapon.caliber}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Registrert av:</span>
                  <span className="text-gray-900">{user?.full_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Tidspunkt:</span>
                  <span className="text-gray-900">{new Date().toLocaleString('nb-NO')}</span>
                </div>
              </div>
            </div>
            
            <button
              onClick={resetWeaponScanner}
              className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-full transition-all duration-200 shadow-lg"
            >
              Skann nytt våpen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render weapon cleaning form
  if (showCleaningForm && scannedWeapon) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-gray-50 rounded-3xl max-w-sm w-full mx-4 shadow-2xl border-4 border-gray-200">
          <div className="flex flex-col p-6">
            <button
              onClick={() => setShowCleaningForm(false)}
              className="self-start mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
              Tilbake
            </button>
            
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-yellow-500 p-3 rounded-full">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Registrer pussing</h2>
                <p className="text-gray-600 text-sm">{scannedWeapon.brand} {scannedWeapon.model}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notater (valgfritt)
                </label>
                <textarea
                  value={cleaningNotes}
                  onChange={(e) => setCleaningNotes(e.target.value)}
                  placeholder="F.eks. renset løp, skiftet fjær..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                />
              </div>
              
              <button
                onClick={handleWeaponCleaning}
                disabled={weaponActionLoading}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold py-4 rounded-xl transition-all duration-200 shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {weaponActionLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Registrerer...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Bekreft pussing
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render weapon maintenance form
  if (showMaintenanceForm && scannedWeapon) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-gray-50 rounded-3xl max-w-sm w-full mx-4 shadow-2xl border-4 border-gray-200">
          <div className="flex flex-col p-6">
            <button
              onClick={() => setShowMaintenanceForm(false)}
              className="self-start mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
              Tilbake
            </button>
            
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-500 p-3 rounded-full">
                <Wrench className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Registrer vedlikehold</h2>
                <p className="text-gray-600 text-sm">{scannedWeapon.brand} {scannedWeapon.model}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beskrivelse av vedlikehold *
                </label>
                <textarea
                  value={maintenanceDescription}
                  onChange={(e) => setMaintenanceDescription(e.target.value)}
                  placeholder="F.eks. byttet sluttstykke, sendt til børsemaker..."
                  rows={3}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kostnad (NOK, valgfritt)
                </label>
                <input
                  type="number"
                  value={maintenanceCost}
                  onChange={(e) => setMaintenanceCost(e.target.value)}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <button
                onClick={handleWeaponMaintenance}
                disabled={weaponActionLoading || !maintenanceDescription}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 rounded-xl transition-all duration-200 shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {weaponActionLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Registrerer...
                  </>
                ) : (
                  <>
                    <Wrench className="w-5 h-5" />
                    Bekreft vedlikehold
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render weapon actions popup
  if (showWeaponActions && scannedWeapon) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-gray-50 rounded-3xl max-w-sm w-full mx-4 shadow-2xl border-4 border-gray-200 relative">
          {/* Close button */}
          <button
            onClick={resetWeaponScanner}
            className="absolute top-4 right-4 p-2 bg-gray-200 hover:bg-gray-300 rounded-full transition-colors z-10"
            aria-label="Lukk"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
          
          <div className="flex flex-col items-center p-8 text-center">
            {/* Weapon Icon */}
            <div className="bg-yellow-500 p-4 rounded-full mb-6">
              <Shield className="w-12 h-12 text-white" />
            </div>
            
            {/* Weapon Info */}
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              {scannedWeapon.brand} {scannedWeapon.model}
            </h2>
            <p className="text-gray-600 mb-2">{scannedWeapon.caliber}</p>
            <p className="text-gray-500 text-sm mb-6 font-mono">{scannedWeapon.serial_number}</p>
            
            {/* Status badge */}
            {scannedWeapon.is_loaned ? (
              <div className="bg-orange-100 text-orange-700 px-4 py-2 rounded-full mb-6 flex items-center gap-2">
                <User className="w-4 h-4" />
                <span className="font-medium">Utlånt til: {scannedWeapon.loaned_to}</span>
              </div>
            ) : (
              <div className="bg-green-100 text-green-700 px-4 py-2 rounded-full mb-6 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Tilgjengelig</span>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="w-full space-y-3 mb-6">
              <button
                onClick={() => setShowCleaningForm(true)}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold py-4 px-4 rounded-2xl text-lg transition-all duration-200 shadow-lg transform hover:scale-105 flex items-center justify-center gap-3"
              >
                <Sparkles className="w-6 h-6" />
                Pussing
              </button>
              
              <button
                onClick={() => setShowMaintenanceForm(true)}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 px-4 rounded-2xl text-lg transition-all duration-200 shadow-lg transform hover:scale-105 flex items-center justify-center gap-3"
              >
                <Wrench className="w-6 h-6" />
                Vedlikehold / Reparasjon
              </button>
              
              {!scannedWeapon.is_loaned && (isAdmin || isOrgAdmin || isRangeOfficer) && (
                <button
                  onClick={() => window.location.href = `/club-operations?action=loan&weapon=${scannedWeapon.id}`}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-4 px-4 rounded-2xl text-lg transition-all duration-200 shadow-lg transform hover:scale-105 flex items-center justify-center gap-3"
                >
                  <User className="w-6 h-6" />
                  Utlån
                </button>
              )}
            </div>
            
            {/* Cancel button */}
            <button
              onClick={resetWeaponScanner}
              className="text-gray-600 hover:text-gray-800 font-medium text-sm transition-colors"
            >
              Skann nytt våpen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render activity selection screen (for training QR codes)
  if (showActivitySelection) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-gray-50 rounded-3xl max-w-sm w-full mx-4 shadow-2xl border-4 border-gray-200">
          <div className="flex flex-col items-center p-8 text-center">
            {/* QR Icon */}
            <div className="bg-yellow-500 p-4 rounded-full mb-6">
              <QrCode className="w-12 h-12 text-white" />
            </div>
            
            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Velg aktivitetstype
            </h2>
            <p className="text-gray-600 mb-8">
              Hva skal du trene i dag?
            </p>
            
            {/* Activity Buttons - 2 column grid for multiple activities */}
            <div className={`w-full mb-6 ${activityTypes.length > 3 ? 'grid grid-cols-2 gap-3' : 'space-y-3'}`}>
              {activityTypes.map((activity, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedActivity(activity);
                    completeRegistration(activity);
                  }}
                  disabled={isProcessing}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold py-4 px-4 rounded-2xl text-base transition-all duration-200 shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isProcessing && selectedActivity === activity ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm">Registrerer...</span>
                    </div>
                  ) : (
                    <span className="break-words">{activity}</span>
                  )}
                </button>
              ))}
            </div>
            
            {/* Cancel button */}
            <button
              onClick={() => {
                setShowActivitySelection(false);
                setPendingSessionData(null);
                setError(null);
              }}
              disabled={isProcessing}
              className="text-gray-600 hover:text-gray-800 font-medium text-sm transition-colors disabled:opacity-50"
            >
              Avbryt
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render success popup (based on provided design)
  if (success && result) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-green-50 rounded-3xl max-w-sm w-full mx-4 shadow-2xl border-4 border-green-200">
          <div className="flex flex-col items-center p-8 text-center">
            {/* Success Icon */}
            <div className="bg-green-500 p-4 rounded-full mb-6">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            
            {/* Success Badge */}
            <div className="bg-green-500 text-white px-8 py-3 rounded-full mb-6 shadow-lg">
              <span className="text-xl font-bold">🎯 TRENING REGISTRERT!</span>
            </div>
            
            {/* Member Info Card */}
            <div className="bg-white rounded-2xl p-6 mb-6 w-full shadow-inner border-2 border-green-100">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Medlem:</span>
                  <span className="text-gray-900 font-bold">{user?.full_name || 'Ukjent medlem'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Dato:</span>
                  <span className="text-gray-900">{new Date().toLocaleDateString('nb-NO')}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Tid:</span>
                  <span className="text-gray-900">{new Date().toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                {result.location && (
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-600">Bane:</span>
                    <span className="text-gray-900 font-medium">{result.location.name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Success Message */}
            <div className="bg-green-100 rounded-xl p-4 mb-6 border-2 border-green-200">
              <p className="text-green-800 font-medium text-lg">
                Treningsøkten er registrert og venter på godkjenning av skyteleder.
              </p>
            </div>
            
            {/* Auto redirect info */}
            <div className="w-full bg-green-200 rounded-full h-3 mb-4 overflow-hidden">
              <div className="bg-green-500 h-3 rounded-full animate-pulse" style={{ width: '100%' }}></div>
            </div>
            <p className="text-sm text-green-700 mb-6">
              Du blir automatisk sendt til treningsloggen om 3 sekunder...
            </p>
            
            {/* Action Button */}
            <button
              onClick={() => window.location.href = '/logg'}
              className="bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-8 rounded-2xl text-lg transition-all duration-200 w-full shadow-lg transform hover:scale-105"
            >
              Gå til Treningslogg
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render already registered popup (based on provided design)  
  if (error && error.includes('allerede registrert')) {
    const timeMatch = error.match(/kl\. (\d{2}:\d{2})/);
    const registeredTime = timeMatch ? timeMatch[1] : '';
    const activityMatch = error.match(/«(.+?)»/);
    const registeredActivity = activityMatch ? activityMatch[1] : '';
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-orange-50 rounded-3xl max-w-sm w-full mx-4 shadow-2xl border-4 border-orange-200">
          <div className="flex flex-col items-center p-8 text-center">
            {/* Warning Icon */}
            <div className="bg-orange-400 p-4 rounded-full mb-6">
              <AlertTriangle className="w-12 h-12 text-white" />
            </div>
            
            {/* Already Registered Badge */}
            <div className="bg-orange-400 text-white px-6 py-3 rounded-full mb-6 shadow-lg">
              <span className="text-xl font-bold">ALLEREDE REGISTRERT!</span>
            </div>
            
            {/* Info Message */}
            <div className="bg-white rounded-2xl p-6 mb-6 w-full shadow-inner border-2 border-orange-100">
              <div className="text-center space-y-3">
                <p className="text-gray-800 font-medium text-lg">
                  Du har allerede registrert {registeredActivity ? <span className="font-bold text-orange-600">«{registeredActivity}»</span> : 'denne aktiviteten'} på denne banen i dag{registeredTime ? <> kl. <span className="font-bold text-orange-600">{registeredTime}</span></> : ''}.
                </p>
                <div className="border-t-2 border-orange-100 pt-3 mt-3">
                  <p className="text-orange-700 font-medium">
                    Du kan registrere en annen aktivitetstype
                  </p>
                </div>
              </div>
            </div>

            {/* Tip Message */}
            <div className="bg-orange-100 rounded-xl p-4 mb-6 border-2 border-orange-200">
              <p className="text-orange-800 font-medium">
                <strong>Tips:</strong> Velg en annen aktivitet for å registrere ny treningsøkt på denne banen i dag!
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="w-full space-y-3">
              <button
                onClick={() => window.location.href = '/logg'}
                className="bg-orange-400 hover:bg-orange-500 text-white font-bold py-3 px-6 rounded-2xl text-lg transition-all duration-200 w-full shadow-lg"
              >
                Se Treningslogg
              </button>
              <button
                onClick={() => {
                  setError(null);
                  setResult(null);
                  setSuccess(false);
                  setIsProcessing(false);
                }}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-3 px-6 rounded-2xl text-base transition-all duration-200 w-full"
              >
                Skann ny QR-kode
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <QrCode className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">QR Scanner</h1>
          <p className="text-gray-400">
            Skann QR-koden for å registrere treningsøkt
          </p>
        </div>

        <div className="bg-gray-800 rounded-lg overflow-hidden">
          {!scanning ? (
            // Start scanning interface
            <div className="flex flex-col items-center p-6 text-center">
              <div className="bg-gray-700 p-4 rounded-full mb-4">
                <QrCode className="w-12 h-12 text-yellow-500" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-4">Start Skanning</h2>
              <p className="text-gray-400 mb-6">
                Trykk for å aktivere kameraet og skanne QR-kode
              </p>
              <button
                onClick={() => {
                  setError(null);
                  setScanning(true);
                  setIsProcessing(false);
                  setScanAttempts(0);
                }}
                className="w-full bg-yellow-500 text-gray-900 py-3 px-6 rounded-lg font-bold hover:bg-yellow-400 transition-colors disabled:opacity-50"
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  'Start Kamera'
                )}
              </button>
            </div>
          ) : (
            // Scanning interface with Bolt.ai styling
            <div className="space-y-4">
              <div className="relative bg-black overflow-hidden aspect-square">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  autoPlay
                  playsInline
                  muted
                />
                
                {/* Processing overlay */}
                {isProcessing && (
                  <div className="absolute inset-0 bg-gray-900/75 flex items-center justify-center">
                    <div className="text-center">
                      <Loader2 className="w-12 h-12 animate-spin text-yellow-500 mx-auto mb-4" />
                      <p className="text-white">Behandler QR-kode...</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Control buttons */}
              <div className="p-4 text-center">
                <p className="text-white mb-4">
                  Rett kameraet mot QR-koden
                </p>
                <button
                  onClick={() => {
                    setScanning(false);
                    setError(null);
                    setIsProcessing(false);
                    setScanAttempts(0);
                  }}
                  className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  disabled={isProcessing}
                >
                  Avbryt Skanning
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mt-4 p-4 bg-red-900/50 border border-red-700 rounded-lg flex items-center gap-2 text-red-200">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        {/* Debug info */}
        {scanAttempts > 0 && (
          <div className="mt-4 p-4 bg-blue-900/50 border border-blue-700 rounded-lg text-blue-200 text-sm">
            <p>Debug: {scanAttempts} scan forsøk</p>
            {lastScannedCode && <p>Siste QR: {lastScannedCode.substring(0, 50)}...</p>}
          </div>
        )}

        {/* Manual input and test buttons removed */}
      </div>
    </div>
  );
};

export default Scanner;